import React, { useContext } from 'react';
import {
  Box,
  Button,
  Text,
  Card,
  Avatar,
  Anchor,
  Grommet,
  Accordion,
  AccordionPanel,
  ThemeType,
} from 'grommet';
import { Youtube, Next } from 'grommet-icons';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDText from '@ui/Text/SHDText';
import SHDButton from '@ui/Button/SHDButton';
import HowToAndErrorModal from '../HowToAndErrorStreamingModal';
import { LiveStreamSettingsModal } from '@features/Socials/LiveStreamSettingsModal';
import { CustomCard } from '@features/Socials/Facebook/FacebookDestinationPicker';
import {
  YOUTUBE_HOW_TO_SET_UP_STREAMING,
  YOUTUBE_STREAMING_ACCESS_ERROR,
  YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_ACCESS_ERROR_TITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE,
} from '@constants/streamingHelpTexts';
import { YoutubeVariant, useYoutubeLinking } from './useYoutube';
import { TeamContext } from '@services/Session/context';
import RoundedToggle from '@components/RoundedToggle';
import { GROMMETTHEME } from '@constants/grommetTheme';
import { VideoSettings } from './VideoSettings';
import { PrivacyModal } from './PrivacyModal';

interface Props {
  variant: YoutubeVariant;
  truncateLength?: number | null;
  liveTeamId?: string;
}

const YoutubeButton: React.FC<Props> = ({
  variant,
  truncateLength = null,
  liveTeamId = null,
}) => {
  const teamStore = useContext(TeamContext);

  const isAdmin = teamStore.isTeamManager;

  const {
    cancelSignin,
    status,
    onUnlinkYoutubeClick,
    updateUnlistedSetting,
    ui: {
      showIntro,
      setShowIntro,
      showHowTo,
      setShowHowTo,
      showError,
      setShowError,
      showSuccess,
      setShowSuccess,
      showPrivacyModal,
      setShowPrivacyModal,
      showVideoSettings,
      setShowVideoSettings,
    },
    handlers: { handleIntroYes, handleIntroNo },
  } = useYoutubeLinking({ variant, liveTeamId });

  const formatTeamName = (name: string) =>
    truncateLength ? name.slice(0, truncateLength) + '...' : name;

  const handleClick = () => {
    if (status?.phase === 'in-progress') {
      cancelSignin();
    } else {
      setShowIntro(true);
    }
  };

  const openChannel = () => {
    window.open(
      `https://www.youtube.com/channel/${status?.channel?.credsChannelId}`,
      '_blank'
    );
  };

  const renderTeamPageButton = () => (
    <Box direction="row" justify="between" align="center">
      {status?.channel ? (
        <a
          href={`https://www.youtube.com/channel/${status.channel.credsChannelId}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button
            primary
            label={
              <SHDText style="footnote-bold">
                {formatTeamName(status.channel.credsChannelName)}
              </SHDText>
            }
            color="#FF0000"
            icon={<Youtube color="white" size="small" />}
          />
        </a>
      ) : (
        isAdmin && (
          <Button
            secondary
            label={
              <SHDText color="#FF0000" style="footnote-bold">
                {status?.phase === 'in-progress'
                  ? 'Cancel connect'
                  : 'Connect YouTube'}
              </SHDText>
            }
            color="#FF0000"
            icon={<Youtube color="#FF0000" size="small" />}
            onClick={handleClick}
          />
        )
      )}
    </Box>
  );

  const renderLivestreamSetupButton = () => (
    <SHDCallout round="medium">
      <Button
        onClick={status?.channel ? openChannel : handleClick}
        style={{ width: '100%' }}
      >
        <Box direction="row" align="center" justify="between" pad="xsmall">
          <Box
            width="48px"
            height="48px"
            round="full"
            align="center"
            justify="center"
            style={{ backgroundColor: status?.channel ? '#FF0000' : '#B6B6B6' }}
          >
            <Youtube
              size="24px"
              color={status?.channel ? 'white' : '#FFFFFF'}
            />
          </Box>
          <Box flex style={{ marginLeft: '16px' }}>
            <SHDText style="subhead-semibold">YouTube</SHDText>
            <SHDText
              style="footnote"
              color={status?.channel ? '#7C7C7C' : '#9E9E9E'}
            >
              {status?.channel
                ? formatTeamName(status.channel.credsChannelName)
                : 'Link account'}
            </SHDText>
          </Box>
          <Box flex={{ shrink: 0, grow: 0 }}>
            <Next size="14px" color="black" />
          </Box>
        </Box>
      </Button>
    </SHDCallout>
  );

  const renderTeamSettingsSkin = () => {
    if (!status?.channel) {
      return (
        isAdmin && (
          <Card background="white" pad="small">
            <Box direction="row" align="center">
              <Box width="medium">
                <Button
                  primary
                  fill="horizontal"
                  color="white"
                  icon={<Youtube color="#FF0000" />}
                  label="Link YouTube"
                  onClick={() => {
                    setShowIntro(true);
                  }}
                />
              </Box>
            </Box>
          </Card>
        )
      );
    }

    const channel = status.channel;

    return (
      <Card background="white" pad="small">
        <Box gap="small" width="medium">
          <Text weight="bold">Streaming to:</Text>
          <Box direction="row" align="center" gap="small" pad="small">
            <Avatar src={channel.channelInfo.snippet.thumbnails.default.url} />
            <Text>{channel.channelInfo.snippet.title}</Text>
            <Anchor
              href={`https://youtube.com/channel/${channel.channelInfo.id}`}
              target="_blank"
            >
              view channel
            </Anchor>
          </Box>
          <Button
            fill="horizontal"
            secondary
            color="status-error"
            icon={<Youtube color="#FF0000" />}
            label="Unlink YouTube"
            onClick={onUnlinkYoutubeClick}
          />
        </Box>
        <Box gap="small" width="medium">
          {channel && (
            <RoundedToggle
              checked={channel.isUnlistedOnConclude}
              label={
                <Text weight={'bold'} size="small">
                  Make unlisted (team community only) after broadcast finishes
                </Text>
              }
              onChange={(e) => {
                if (e.target.checked) {
                  setShowPrivacyModal(true);
                } else {
                  updateUnlistedSetting(false);
                }
              }}
            />
          )}
        </Box>
        <Grommet
          style={{ minHeight: 0 }}
          theme={
            {
              ...GROMMETTHEME,
              accordion: {
                border: { color: 'transparent' },
                icons: { color: 'light-6' },
              },
            } as unknown as ThemeType
          }
        >
          <Box gap="small" width="medium">
            <Accordion margin={{ top: 'small' }}>
              <AccordionPanel
                label={
                  <Text size="8px" color="gray">
                    Advanced Settings
                  </Text>
                }
              >
                <Button
                  secondary
                  color="light-2"
                  label="Video Settings"
                  onClick={() => {
                    setShowVideoSettings(true);
                  }}
                />
              </AccordionPanel>
            </Accordion>
          </Box>
        </Grommet>
      </Card>
    );
  };

  const renderButton = () => {
    switch (variant) {
      case 'team-page':
        return renderTeamPageButton();
      case 'team-settings':
        return renderTeamSettingsSkin();
      case 'livestream-setup':
        return renderLivestreamSetupButton();
      default:
        break;
    }
  };

  return (
    <>
      {renderButton()}

      {showIntro && (
        <LiveStreamSettingsModal
          headerText="LINK YOUTUBE"
          title="Have you ever livestreamed on YouTube?"
          onCloseModal={() => setShowIntro(false)}
        >
          <SHDButton
            secondary
            label={
              <SHDText size="button" style="subhead-bold" textAlign="center">
                Yes, I have YouTube Studio set up
              </SHDText>
            }
            onClick={handleIntroYes}
          />
          <SHDButton
            secondary
            label={
              <SHDText size="button" style="subhead-bold" textAlign="center">
                No, I need help setting up
              </SHDText>
            }
            onClick={handleIntroNo}
          />
        </LiveStreamSettingsModal>
      )}

      {showHowTo && (
        <HowToAndErrorModal
          headerText="LINK YOUTUBE"
          title="How to set up YouTube livestreaming"
          subtitle=""
          onClose={() => setShowHowTo(false)}
          isError={false}
          htmlContent={YOUTUBE_HOW_TO_SET_UP_STREAMING}
        />
      )}

      {showError && status?.error && (
        <HowToAndErrorModal
          headerText="LINK YOUTUBE"
          title={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_TITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE
          }
          subtitle={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE
          }
          onClose={() => setShowError(false)}
          isError
          htmlContent={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR
          }
        />
      )}

      {showSuccess && status?.channel && (
        <LiveStreamSettingsModal
          headerText="LINK YOUTUBE"
          title={`You can now stream to ${status.channel.channelInfo.snippet.title}`}
          onCloseModal={() => setShowSuccess(false)}
        >
          <Box style={{ marginTop: -20 }} gap="large" fill="vertical">
            <SHDText style="footnote">
              Sideline HD will now stream to the following channel
            </SHDText>
            <Box gap="large" flex="grow" overflow="auto">
              <CustomCard
                onClick={() => {}}
                name={status.channel.channelInfo.snippet.title}
                subtitle=""
                disabled={false}
                pictureUrl={
                  status.channel.channelInfo.snippet.thumbnails.default.url
                }
              />
            </Box>
            <SHDButton
              primary
              label="Done"
              onClick={() => setShowSuccess(false)}
            />
          </Box>
        </LiveStreamSettingsModal>
      )}
      {showVideoSettings && (
        <VideoSettings
          onClose={() => {
            setShowVideoSettings(false);
          }}
        />
      )}
      {showPrivacyModal && (
        <PrivacyModal
          onUpdateUnlistedSetting={updateUnlistedSetting}
          onClose={() => {
            setShowPrivacyModal(false);
          }}
        />
      )}
    </>
  );
};

export default YoutubeButton;
